import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Bug } from 'lucide-react';
import { useStore } from '../../store';
import { DaMonogramIcon } from '../ui/DaMonogramIcon';
import { ReportIssueModal } from '../siterag/ReportBugButton';
import { ChatPanel } from '../chat/ChatPanel';

/**
 * App-wide dual-pill launcher, docked in the simulation header when supplied.
 *
 * Segment 1 ("Ask DAM") opens the Datacrate chat assistant (ChatPanel,
 * which posts to the Ask DAM Cloud Function). Segment 2 (bug icon)
 * triggers the existing report-issue flow — preserved exactly, just moved
 * into the right pill segment so there's a single utility launcher.
 */
export function GlobalReportIssueButton({
  launcherSlot = null,
}: { launcherSlot?: HTMLElement | null }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const activeSiteId = useStore(s => s.siteRag.activeSiteId);
  const sites = useStore(s => s.siteRag.sites);
  const companyId = useStore(s => s.companyId);
  const openChat = useStore(s => s.openChat);
  // Hide the FAB while the chat panel is open — on phones the panel is
  // full-width and the pill would otherwise float over its own
  // content/close button.
  const isChatOpen = useStore(s => s.isOpen);

  // Resolve the active site's display name so the chat scopes/labels itself
  // to the open site (falls back to the id if the site doc isn't loaded yet).
  const activeSiteLabel = useMemo(() => {
    if (!activeSiteId) return null;
    return sites.find(s => s.id === activeSiteId)?.name ?? activeSiteId;
  }, [activeSiteId, sites]);

  // Editor history/results can fill the entire pane, including the lower right
  // corner. Dock the same launcher in its header so exact-run actions remain
  // clickable without reserving canvas space or resetting chat/modal state.
  const launcher = !isChatOpen && (
    <div
      className={`${launcherSlot ? 'relative shrink-0' : 'fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom))] right-6 z-40'} flex items-stretch overflow-hidden rounded-full border border-border-strong bg-surface-1 text-primary shadow-xl`}
      data-testid="global-fab-dual-pill"
      data-placement={launcherSlot ? 'header' : 'floating'}
    >
      <button
        type="button"
        aria-label="Ask DAM — Datacrate assistant"
        className="flex items-center justify-center px-3 py-2.5 text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        onClick={() => openChat()}
        data-testid="global-ask-dam-button"
      >
        <DaMonogramIcon className="w-4 h-4 shrink-0" aria-hidden />
      </button>
      <span className="w-px self-stretch bg-border-strong" aria-hidden />
      <button
        type="button"
        aria-label="Report issue"
        className="flex items-center justify-center px-3 py-2.5 text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        onClick={() => setOpen(true)}
        data-testid="global-report-issue-button"
      >
        <Bug className="w-4 h-4" aria-hidden />
      </button>
    </div>
  );

  return (
    <>
      {launcherSlot ? createPortal(launcher, launcherSlot) : launcher}
      <ChatPanel
        routeSiteId={activeSiteId}
        routeSiteLabel={activeSiteLabel}
        canShareWithTeam={Boolean(companyId)}
      />
      <ReportIssueModal
        open={open}
        onClose={() => setOpen(false)}
        siteId={activeSiteId ?? ''}
        context={{
          source: 'global-fab',
          siteId: activeSiteId ?? undefined,
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          currentRoute: location.pathname + location.search,
        }}
      />
    </>
  );
}

/** @deprecated Alias — use GlobalReportIssueButton. */
export const FeedbackButton = GlobalReportIssueButton;
